import React, { useMemo, useState } from 'react';
import { ResponsiveBoxPlot } from '@nivo/boxplot';
import { RootCauseBoxPlotGraphResult } from 'libs/end-to-end/src/root-cause-analysis';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { z } from 'zod';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';

export const BoxPlotLayer = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
});
export type BoxPlotLayer = z.infer<typeof BoxPlotLayer>;

export const ThresholdAnalysisBoxPlot: React.FC<{
  result: RootCauseBoxPlotGraphResult;
  xAxisText: string;
  treatmentExplanationTranslatedContent: TranslatedContent;
  referenceExplanationTranslatedContent: TranslatedContent;
  gapExplanationTranslatedContent: TranslatedContent;
}> = ({
  result,
  xAxisText,
  treatmentExplanationTranslatedContent,
  referenceExplanationTranslatedContent,
  gapExplanationTranslatedContent,
}) => {
  const data = useMemo(() => {
    return [
      {
        group: 'A',
        value: result.treatment_threshold,
      },
      {
        group: 'A',
        value: result.reference_threshold,
      },
    ];
  }, [result]);

  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    content: React.ReactNode;
  } | null>(null);

  const customLayer = useMemo(
    () =>
      ({ boxPlots }: { boxPlots: BoxPlotLayer[] }) => {
        const treatIsLeft =
          result.treatment_threshold < result.reference_threshold;
        return boxPlots.map((boxPlot: BoxPlotLayer, index: number) => (
          <g key={index}>
            {/* Right point to symbolize reference group */}
            <circle
              id="right-group"
              cx={boxPlot.x + boxPlot.width}
              cy={boxPlot.y + boxPlot.height / 2}
              r={8}
              fill={treatIsLeft ? '#076ed5' : '#6e46e7'}
              stroke="black"
              strokeWidth={1}
              pointerEvents="visiblePainted"
              onPointerEnter={(e) => {
                setTooltip({
                  x: e.clientX,
                  y: e.clientY,
                  content: treatIsLeft ? (
                    <S2Typography
                      variant="body-6"
                      translatedContent={referenceExplanationTranslatedContent}
                    />
                  ) : (
                    <S2Typography
                      variant="body-6"
                      translatedContent={treatmentExplanationTranslatedContent}
                    />
                  ),
                });
              }}
              onPointerLeave={() => {
                setTooltip(null);
              }}
            />
            {/* Left point to symbolize treatment group */}
            <circle
              id="left-group"
              cx={boxPlot.x}
              cy={boxPlot.y + boxPlot.height / 2}
              r={8}
              fill={treatIsLeft ? '#6e46e7' : '#076ed5'}
              stroke="black"
              strokeWidth={1}
              pointerEvents="visiblePainted"
              onPointerEnter={(e) => {
                setTooltip({
                  x: e.clientX,
                  y: e.clientY,
                  content: treatIsLeft ? (
                    <S2Typography
                      variant="body-6"
                      translatedContent={treatmentExplanationTranslatedContent}
                    />
                  ) : (
                    <S2Typography
                      variant="body-6"
                      translatedContent={referenceExplanationTranslatedContent}
                    />
                  ),
                });
              }}
              onPointerLeave={() => {
                setTooltip(null);
              }}
            />
            {/* Gap point in the middle */}
            <text
              x={boxPlot.x + boxPlot.width / 2}
              y={boxPlot.y + boxPlot.height / 2 + 5}
              textAnchor="middle"
              fontSize={13}
              fill="#000"
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onPointerEnter={(e) =>
                setTooltip({
                  x: e.clientX,
                  y: e.clientY,
                  content: (
                    <S2Typography
                      variant="body-6"
                      translatedContent={gapExplanationTranslatedContent}
                    />
                  ),
                })
              }
              onPointerLeave={() => setTooltip(null)}
            >
              GAP
            </text>
          </g>
        ));
      },
    [
      result,
      treatmentExplanationTranslatedContent,
      referenceExplanationTranslatedContent,
      gapExplanationTranslatedContent,
      setTooltip,
    ],
  );

  return (
    <div style={{ position: 'relative', width: '100%', height: 180 }}>
      <ResponsiveBoxPlot
        data={data}
        margin={{ top: 60, right: 140, bottom: 60, left: 60 }}
        minValue={result.x_min}
        maxValue={result.x_max}
        quantiles={[0, 0, 0.5, 1, 1]}
        layout="horizontal"
        enableGridX={true}
        enableGridY={true}
        axisBottom={{
          legend: xAxisText,
          legendPosition: 'middle',
          legendOffset: 32,
        }}
        axisLeft={null}
        colors={['#d7d9da']}
        colorBy="group"
        borderRadius={2}
        borderColor={{ from: 'color', modifiers: [] }}
        medianWidth={0}
        medianColor={{ from: 'color', modifiers: [['darker', 0]] }}
        whiskerEndSize={0.3}
        whiskerColor={'#000000'}
        whiskerWidth={1}
        isInteractive={false}
        opacity={0.65}
        layers={['grid', 'axes', 'boxPlots', customLayer, 'legends']}
        legends={[
          {
            anchor: 'bottom-right',
            direction: 'column',
            translateX: 100,
            itemWidth: 80,
            itemHeight: 20,
            itemsSpacing: 3,
            data: [
              {
                id: 'treatment',
                label:
                  result.visualizationAttributes?.treatmentLevel ??
                  'Treatment Group',
                color: '#6e46e7',
              },
              {
                id: 'reference',
                label:
                  result.visualizationAttributes?.referenceLevel ??
                  'Reference Group',
                color: '#076ed5',
              },
            ],
          },
        ]}
      />
      {tooltip && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x + 10,
            top: tooltip.y + 10,
            background: 'white',
            border: '1px solid #ccc',
            padding: '6px 12px',
            borderRadius: 4,
            pointerEvents: 'none',
            zIndex: 10,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          {tooltip.content}
        </div>
      )}
    </div>
  );
};
